/**
 * Drone Catalog Admin Page - Wave 3 Block 3B
 * 
 * Platform admin page for managing drone catalog entries.
 * - List all catalog entries (active + deprecated)
 * - Add new entries
 * - Edit existing entries
 * - Mark entries as deprecated (soft delete)
 * 
 * RBAC: platform_admin only
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import {
  ArrowLeft, Plus, Pencil, Archive, Search, Filter,
  Loader2, AlertCircle, Plane, CheckCircle, XCircle,
  Clock, Settings, Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const DroneCatalogPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState('');
  const [includeDeprecated, setIncludeDeprecated] = useState(false);
  const [manufacturerFilter, setManufacturerFilter] = useState('all');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    id: '',
    manufacturer: '',
    model: '',
    full_name: '',
    service_interval_hours: 200,
    service_interval_days: 365,
    battery_cycle_warning: 200,
    battery_cycle_replacement: 300,
    propeller_replacement_hours: 50,
    default_rid_type: 'non_compliant',
    default_rid_compliant: false,
    rid_notes: '',
    mtom_kg: '',
    max_speed_kmh: '',
    max_wind_resistance_ms: '',
    ingress_protection: '',
    notes: '',
    source_url: '',
  });
  
  const tr = t.catalog || {};
  const trAdmin = t.admin?.droneCatalog || {};
  
  // Check platform admin
  const isPlatformAdmin = user?.role === 'platform_admin';
  
  // Fetch entries
  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (includeDeprecated) params.append('include_deprecated', 'true');
      if (manufacturerFilter !== 'all') params.append('manufacturer', manufacturerFilter);
      
      const response = await api.get(`/drone-catalog?${params.toString()}`);
      setEntries(response.data);
    } catch (err) {
      console.error('Error fetching catalog:', err);
      toast.error(t.messages?.fetchError || 'Failed to load catalog');
    } finally {
      setLoading(false);
    }
  }, [includeDeprecated, manufacturerFilter, t.messages?.fetchError]);
  
  useEffect(() => {
    if (!authLoading && !isPlatformAdmin) {
      navigate('/');
      return;
    }
    if (!authLoading) {
      fetchEntries();
    }
  }, [authLoading, isPlatformAdmin, navigate, fetchEntries]);
  
  // Get unique manufacturers for filter
  const manufacturers = [...new Set(entries.map(e => e.manufacturer))].sort();
  
  // Filter entries
  const filteredEntries = entries.filter(entry => {
    const searchLower = search.toLowerCase();
    return (
      entry.full_name.toLowerCase().includes(searchLower) ||
      entry.manufacturer.toLowerCase().includes(searchLower) ||
      entry.model.toLowerCase().includes(searchLower) ||
      entry.id.toLowerCase().includes(searchLower)
    );
  });
  
  // Reset form
  const resetForm = () => {
    setFormData({
      id: '',
      manufacturer: '',
      model: '',
      full_name: '',
      service_interval_hours: 200,
      service_interval_days: 365,
      battery_cycle_warning: 200,
      battery_cycle_replacement: 300,
      propeller_replacement_hours: 50,
      default_rid_type: 'non_compliant',
      default_rid_compliant: false,
      rid_notes: '',
      mtom_kg: '',
      max_speed_kmh: '',
      max_wind_resistance_ms: '',
      ingress_protection: '',
      notes: '',
      source_url: '',
    });
    setEditingEntry(null);
  };
  
  // Open add modal
  const handleAdd = () => {
    resetForm();
    setShowModal(true);
  };
  
  // Open edit modal
  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      id: entry.id,
      manufacturer: entry.manufacturer,
      model: entry.model,
      full_name: entry.full_name,
      service_interval_hours: entry.service_interval_hours,
      service_interval_days: entry.service_interval_days,
      battery_cycle_warning: entry.battery_cycle_warning,
      battery_cycle_replacement: entry.battery_cycle_replacement,
      propeller_replacement_hours: entry.propeller_replacement_hours,
      default_rid_type: entry.default_rid_type,
      default_rid_compliant: entry.default_rid_compliant,
      rid_notes: entry.rid_notes || '',
      mtom_kg: entry.mtom_kg || '',
      max_speed_kmh: entry.max_speed_kmh || '',
      max_wind_resistance_ms: entry.max_wind_resistance_ms || '',
      ingress_protection: entry.ingress_protection || '',
      notes: entry.notes || '',
      source_url: entry.source_url || '',
    });
    setShowModal(true);
  };
  
  // Auto-generate ID from manufacturer + model
  const generateId = (manufacturer, model) => {
    return `${manufacturer}_${model}`.toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  };
  
  // Handle manufacturer/model change
  const handleManufacturerModelChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    
    // Auto-update full_name and id if creating new
    if (!editingEntry) {
      if (field === 'manufacturer' || field === 'model') {
        const mfr = field === 'manufacturer' ? value : formData.manufacturer;
        const mdl = field === 'model' ? value : formData.model;
        newData.full_name = `${mfr} ${mdl}`.trim();
        newData.id = generateId(mfr, mdl);
      }
    }
    
    setFormData(newData);
  };
  
  // Save entry
  const handleSave = async () => {
    // Validation
    if (!formData.id || !formData.manufacturer || !formData.model || !formData.full_name) {
      toast.error(t.validation?.requiredFields || 'Please fill all required fields');
      return;
    }
    
    try {
      setSaving(true);
      
      const payload = {
        ...formData,
        mtom_kg: formData.mtom_kg ? parseFloat(formData.mtom_kg) : null,
        max_speed_kmh: formData.max_speed_kmh ? parseInt(formData.max_speed_kmh) : null,
        max_wind_resistance_ms: formData.max_wind_resistance_ms ? parseInt(formData.max_wind_resistance_ms) : null,
      };
      
      if (editingEntry) {
        // Update
        await api.patch(`/admin/drone-catalog/${editingEntry.id}`, payload);
        toast.success(t.messages?.saveSuccess || 'Catalog entry updated');
      } else {
        // Create
        await api.post('/admin/drone-catalog', payload);
        toast.success(t.messages?.saveSuccess || 'Catalog entry created');
      }
      
      setShowModal(false);
      resetForm();
      fetchEntries();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail?.message_cs || detail?.message_en || t.messages?.saveError || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };
  
  // Deprecate entry
  const handleDeprecate = async (entry) => {
    if (!window.confirm(`${t.common?.confirmDelete || 'Are you sure?'}\n\n${entry.full_name}`)) {
      return;
    }
    
    try {
      await api.delete(`/admin/drone-catalog/${entry.id}`);
      toast.success(t.messages?.deleteSuccess || 'Entry deprecated');
      fetchEntries();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail?.message_cs || detail?.message_en || t.messages?.deleteError || 'Failed to deprecate');
    }
  };
  
  // RID type badge
  const getRidBadge = (ridType, compliant) => {
    if (ridType === 'built_in' && compliant) {
      return <Badge className="bg-emerald-100 text-emerald-800">{tr.ridBuiltIn || 'Built-in'}</Badge>;
    }
    if (ridType === 'modular') {
      return <Badge className="bg-blue-100 text-blue-800">{tr.ridModular || 'Modular'}</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-600">{tr.ridNonCompliant || 'Non-compliant'}</Badge>;
  };
  
  // Loading state
  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  // Access denied
  if (!isPlatformAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <h1 className="text-xl font-semibold">{t.common?.accessDenied || 'Access Denied'}</h1>
        <p className="text-slate-500">{t.common?.platformAdminOnly || 'Platform admin access required'}</p>
        <Button onClick={() => navigate('/')}>{t.common?.goBack || 'Go Back'}</Button>
      </div>
    );
  }
  
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Plane className="h-6 w-6 text-blue-600" />
              {trAdmin.title || tr.adminTitle || 'Drone Catalog'}
            </h1>
            <p className="text-slate-500">{trAdmin.subtitle || 'Manage global drone catalog'}</p>
          </div>
        </div>
        <Button onClick={handleAdd} className="gap-2" data-testid="add-catalog-entry-btn">
          <Plus className="h-4 w-4" />
          {tr.addEntry || 'Add Entry'}
        </Button>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Plane className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{entries.length}</p>
                <p className="text-sm text-slate-500">{trAdmin.totalEntries || 'Total entries'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{entries.filter(e => !e.deprecated).length}</p>
                <p className="text-sm text-slate-500">{trAdmin.activeEntries || 'Active'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-100 rounded-lg">
                <Archive className="h-6 w-6 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{entries.filter(e => e.deprecated).length}</p>
                <p className="text-sm text-slate-500">{trAdmin.deprecatedEntries || 'Deprecated'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t.common?.search || 'Search...'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-catalog"
              />
            </div>
            <Select value={manufacturerFilter} onValueChange={setManufacturerFilter}>
              <SelectTrigger className="w-40" data-testid="manufacturer-filter">
                <SelectValue placeholder={tr.manufacturer || 'Manufacturer'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.common?.all || 'All'}</SelectItem>
                {manufacturers.map(mfr => (
                  <SelectItem key={mfr} value={mfr}>{mfr}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Switch
                id="deprecated"
                checked={includeDeprecated}
                onCheckedChange={setIncludeDeprecated}
                data-testid="include-deprecated-switch"
              />
              <Label htmlFor="deprecated" className="text-sm">{tr.includeDeprecated || 'Show deprecated'}</Label>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{tr.fullName || 'Name'}</TableHead>
                <TableHead>{tr.manufacturer || 'Manufacturer'}</TableHead>
                <TableHead>{tr.serviceIntervalHours || 'Service (hrs)'}</TableHead>
                <TableHead>{tr.mtomKg || 'MTOM (kg)'}</TableHead>
                <TableHead>{tr.defaultRidType || 'RID'}</TableHead>
                <TableHead>{t.common?.status || 'Status'}</TableHead>
                <TableHead className="w-24">{t.common?.actions || 'Actions'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    {t.common?.noResults || 'No entries found'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredEntries.map((entry) => (
                  <TableRow key={entry.id} className={entry.deprecated ? 'opacity-50' : ''} data-testid={`catalog-row-${entry.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{entry.full_name}</p>
                        <p className="text-xs text-slate-400 font-mono">{entry.id}</p>
                      </div>
                    </TableCell>
                    <TableCell>{entry.manufacturer}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <span className="font-medium">{entry.service_interval_hours}h</span>
                        <span className="text-slate-400 ml-1">/ {entry.service_interval_days}d</span>
                      </div>
                    </TableCell>
                    <TableCell>{entry.mtom_kg ? `${entry.mtom_kg} kg` : '-'}</TableCell>
                    <TableCell>{getRidBadge(entry.default_rid_type, entry.default_rid_compliant)}</TableCell>
                    <TableCell>
                      {entry.deprecated ? (
                        <Badge variant="outline" className="text-slate-500">{tr.deprecated || 'Deprecated'}</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-800">{t.common?.active || 'Active'}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(entry)} data-testid={`edit-${entry.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {!entry.deprecated && (
                          <Button variant="ghost" size="icon" onClick={() => handleDeprecate(entry)} className="text-amber-600" data-testid={`deprecate-${entry.id}`}>
                            <Archive className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) { setShowModal(false); resetForm(); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? (tr.editEntry || 'Edit Entry') : (tr.addEntry || 'Add Entry')}
            </DialogTitle>
            <DialogDescription>
              {editingEntry ? editingEntry.full_name : (t.common?.fillFields || 'Fill in the details below')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{tr.manufacturer || 'Manufacturer'} *</Label>
                <Input
                  value={formData.manufacturer}
                  onChange={(e) => handleManufacturerModelChange('manufacturer', e.target.value)}
                  placeholder="DJI, Autel, Parrot..."
                  data-testid="form-manufacturer"
                />
              </div>
              <div className="space-y-2">
                <Label>{tr.model || 'Model'} *</Label>
                <Input
                  value={formData.model}
                  onChange={(e) => handleManufacturerModelChange('model', e.target.value)}
                  placeholder="Matrice 30T"
                  data-testid="form-model"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{tr.fullName || 'Full Name'} *</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="DJI Matrice 30T"
                  data-testid="form-full-name"
                />
              </div>
              <div className="space-y-2">
                <Label>ID *</Label>
                <Input
                  value={formData.id}
                  onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                  placeholder="dji_matrice_30t"
                  disabled={!!editingEntry}
                  className={editingEntry ? 'bg-slate-100' : ''}
                  data-testid="form-id"
                />
              </div>
            </div>
            
            {/* Service Intervals */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Settings className="h-4 w-4" />
                {t.common?.serviceIntervals || 'Service Intervals'}
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{tr.serviceIntervalHours || 'Hours'}</Label>
                  <Input
                    type="number"
                    value={formData.service_interval_hours}
                    onChange={(e) => setFormData({ ...formData, service_interval_hours: parseInt(e.target.value) || 0 })}
                    data-testid="form-service-hours"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{tr.serviceIntervalDays || 'Days'}</Label>
                  <Input
                    type="number"
                    value={formData.service_interval_days}
                    onChange={(e) => setFormData({ ...formData, service_interval_days: parseInt(e.target.value) || 0 })}
                    data-testid="form-service-days"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{tr.propellerReplacementHours || 'Propeller (hrs)'}</Label>
                  <Input
                    type="number"
                    value={formData.propeller_replacement_hours}
                    onChange={(e) => setFormData({ ...formData, propeller_replacement_hours: parseInt(e.target.value) || 0 })}
                    data-testid="form-propeller-hours"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>{tr.batteryCycleWarning || 'Battery Cycle Warning'}</Label>
                  <Input
                    type="number"
                    value={formData.battery_cycle_warning}
                    onChange={(e) => setFormData({ ...formData, battery_cycle_warning: parseInt(e.target.value) || 0 })}
                    data-testid="form-battery-warning"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{tr.batteryCycleReplacement || 'Battery Replacement'}</Label>
                  <Input
                    type="number"
                    value={formData.battery_cycle_replacement}
                    onChange={(e) => setFormData({ ...formData, battery_cycle_replacement: parseInt(e.target.value) || 0 })}
                    data-testid="form-battery-replacement"
                  />
                </div>
              </div>
            </div>
            
            {/* RID Info */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Shield className="h-4 w-4" />
                {t.common?.remoteId || 'Remote ID'}
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{tr.defaultRidType || 'RID Type'}</Label>
                  <Select
                    value={formData.default_rid_type}
                    onValueChange={(v) => setFormData({ ...formData, default_rid_type: v, default_rid_compliant: v === 'built_in' })}
                  >
                    <SelectTrigger data-testid="form-rid-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="built_in">{tr.ridBuiltIn || 'Built-in'}</SelectItem>
                      <SelectItem value="modular">{tr.ridModular || 'Modular'}</SelectItem>
                      <SelectItem value="non_compliant">{tr.ridNonCompliant || 'Non-compliant'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t.common?.notes || 'RID Notes'}</Label>
                  <Input
                    value={formData.rid_notes}
                    onChange={(e) => setFormData({ ...formData, rid_notes: e.target.value })}
                    placeholder="Built-in with firmware v1.0+"
                    data-testid="form-rid-notes"
                  />
                </div>
              </div>
            </div>
            
            {/* Specs */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">{t.common?.specifications || 'Specifications'}</h4>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>{tr.mtomKg || 'MTOM (kg)'}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.mtom_kg}
                    onChange={(e) => setFormData({ ...formData, mtom_kg: e.target.value })}
                    data-testid="form-mtom"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{tr.maxSpeedKmh || 'Speed (km/h)'}</Label>
                  <Input
                    type="number"
                    value={formData.max_speed_kmh}
                    onChange={(e) => setFormData({ ...formData, max_speed_kmh: e.target.value })}
                    data-testid="form-speed"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{tr.maxWindMs || 'Wind (m/s)'}</Label>
                  <Input
                    type="number"
                    value={formData.max_wind_resistance_ms}
                    onChange={(e) => setFormData({ ...formData, max_wind_resistance_ms: e.target.value })}
                    data-testid="form-wind"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{tr.ingressProtection || 'IP'}</Label>
                  <Input
                    value={formData.ingress_protection}
                    onChange={(e) => setFormData({ ...formData, ingress_protection: e.target.value })}
                    placeholder="IP55"
                    data-testid="form-ip"
                  />
                </div>
              </div>
            </div>
            
            {/* Notes */}
            <div className="border-t pt-4">
              <div className="space-y-2">
                <Label>{t.common?.notes || 'Notes'}</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  data-testid="form-notes"
                />
              </div>
              <div className="space-y-2 mt-4">
                <Label>{t.common?.sourceUrl || 'Source URL'}</Label>
                <Input
                  value={formData.source_url}
                  onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
                  placeholder="https://www.dji.com/matrice-30/specs"
                  data-testid="form-source-url"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>
              {t.actions?.cancel || 'Cancel'}
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-catalog-entry-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t.actions?.save || 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DroneCatalogPage;
